"use client";

import Image from "next/image";
import { useState } from "react";
import { useCarouselDrag } from "@/app/hooks/useCarouselDrag";

const estruturas = [
  {
    id: 1,
    image: "/images/home/agi-campus-1.png",
    label: "Sede do corporativo",
    icon: "corporativo",
  },
  {
    id: 2,
    image: "/images/home/agi-campus-2.png",
    label: "Sede do corporativo",
    icon: "corporativo",
  },
  {
    id: 3,
    image: "/images/home/loja1000.png",
    label: "Loja Agibank",
    icon: "loja",
  },
  {
    id: 4,
    image: "/images/home/Loja1000_01.png",
    label: "Loja Agibank",
    icon: "loja",
  },
  {
    id: 5,
    image: "/images/home/Loja1000_03.png",
    label: "Loja Agibank",
    icon: "loja",
  },
];

interface NossaEstruturaProps {
  variant?: "default" | "light" | "dark";
  title?: string;
  description?: string;
}

export default function NossaEstrutura({
  variant = "default",
  title = "Nossa estrutura",
  description,
}: NossaEstruturaProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? estruturas.length - 1 : prev - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prev) =>
      prev === estruturas.length - 1 ? 0 : prev + 1
    );
  };

  const getSlideIndex = (offset: number) => {
    let index = currentIndex + offset;
    if (index < 0) index = estruturas.length + index;
    if (index >= estruturas.length) index = index - estruturas.length;
    return index;
  };

  const dragProps = useCarouselDrag({
    onSwipeLeft: goToNext,
    onSwipeRight: goToPrevious,
  });

  const isLight = variant === "light";
  const isDark = variant === "dark";

  return (
    <section
      className={`py-16 lg:py-24 overflow-hidden ${
        isDark ? "bg-blue-dark" : isLight ? "bg-white" : "bg-primary-main"
      }`}
    >
      <div className="max-w-[1428px] mx-auto px-4">
        {/* Title */}
        <h2
          className={`text-[36px] lg:text-[43px] font-bold text-center mb-4 ${
            isDark ? "text-white" : isLight ? "text-primary-main" : "text-white"
          }`}
        >
          {title}
        </h2>
        {description && (
          <p
            className={`text-center text-base lg:text-lg mb-10 lg:mb-14 max-w-3xl mx-auto ${
              isDark ? "text-secondary-main" : isLight ? "text-blue-dark" : "text-white/90"
            }`}
          >
            {description}
          </p>
        )}
        {!description && <div className="mb-6 lg:mb-10" />}

        {/* Carousel */}
        <div className="relative h-[400px] lg:h-[480px]">
          {/* Slides Container */}
          <div className="absolute inset-0 flex items-center justify-center select-none" {...dragProps}>
            {/* Far left slide (-2) */}
            <div
              className="hidden lg:block absolute left-0 w-[200px] h-[280px] rounded-[40px] overflow-hidden transition-all duration-500"
              style={{ opacity: 0.4 }}
            >
              <Image
                src={estruturas[getSlideIndex(-2)].image}
                alt={estruturas[getSlideIndex(-2)].label}
                fill
                className="object-cover"
              />
            </div>

            {/* Left slide (-1) */}
            <div
              className="hidden lg:block absolute left-[120px] w-[320px] h-[360px] rounded-[40px] overflow-hidden transition-all duration-500"
              style={{ opacity: 1 }}
            >
              <Image
                src={estruturas[getSlideIndex(-1)].image}
                alt={estruturas[getSlideIndex(-1)].label}
                fill
                className="object-cover"
              />
            </div>

            {/* Center slide (current) */}
            <div className="relative z-10 w-full max-w-[580px] lg:max-w-[720px] h-[260px] lg:h-[380px] rounded-[40px] overflow-hidden shadow-2xl transition-all duration-500">
              <Image
                src={estruturas[currentIndex].image}
                alt={estruturas[currentIndex].label}
                fill
                className="object-cover"
                priority
              />
              {/* Label badge */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-blue-main text-white px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
                {estruturas[currentIndex].icon === "corporativo" ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5.30587 18.271C4.22987 18.278 3.90015 17.7607 3.90015 16.7959V3.12384C3.90015 2.37065 4.54227 1.72852 5.32323 1.72852H14.2679C15.4375 1.72852 16.1005 2.34288 16.1005 3.54035C16.1005 3.54035 16.1005 15.9674 16.1005 16.7959C16.1005 17.6244 15.7341 18.271 14.8527 18.271C13.9714 18.271 11.6924 18.2571 11.6924 18.2571C10.8871 18.2571 10.6511 17.858 10.6511 17.1637V14.6092C10.6511 14.1475 10.4602 13.7415 9.80767 13.7415L7.95767 13.7622C7.4162 13.7622 7.142 14.0607 7.142 14.5745V17.2922C7.142 17.9135 6.89556 18.2571 6.23608 18.2606C6.23608 18.2606 6.38186 18.2641 5.30587 18.271ZM8.30476 7.06335C8.67615 7.06335 8.99895 6.75443 8.99895 6.36917V4.63369C8.99895 4.24842 8.67615 3.9395 8.30476 3.9395H6.45128C6.01742 3.9395 5.67032 4.28313 5.67032 4.72047V6.28239C5.67032 6.71973 6.01395 7.06335 6.45128 7.06335H8.30476ZM9.55082 11.0376V9.06608C9.55082 8.62874 9.28356 8.37189 8.85663 8.37189H6.68731C6.1979 8.37189 5.90635 8.66345 5.90635 9.15285V10.9508C5.90635 11.4402 6.1979 11.7318 6.68731 11.7318H8.85663C9.28356 11.7318 9.55082 11.4749 9.55082 11.0376ZM13.525 7.14318C13.9485 7.14318 14.2192 6.88633 14.2192 6.44899V4.4775C14.2192 4.04017 13.9485 3.78332 13.525 3.78332H11.3557C10.8663 3.78332 10.5748 4.07488 10.5748 4.56428V6.36223C10.5748 6.85163 10.9184 7.14318 11.3557 7.14318H13.525ZM13.4904 9.84704V8.98278C13.4904 8.54544 13.2474 8.28859 12.7961 8.28859H11.734C11.2446 8.28859 10.9531 8.58014 10.9531 9.06955V9.76027C10.9531 10.2774 11.2967 10.5412 11.734 10.5412H12.7961C13.1675 10.5412 13.4904 10.3364 13.4904 9.84704Z" fill="white"/>
                </svg>) : (

                <svg xmlns="http://www.w3.org/2000/svg" enable-background="new 0 0 24 24" height="24" viewBox="0 0 24 24" width="20">
                  <g><rect fill="none" height="24" width="24"/></g><g><g><rect height="7" width="3" x="4" y="10" fill="white"/><rect height="7" width="3" x="10.5" y="10" fill="white"/><rect height="3" width="20" x="2" y="19" fill="white"/><rect height="7" width="3" x="17" y="10" fill="white"/><polygon points="12,1 2,6 2,8 22,8 22,6" fill="white"/></g></g></svg>
                )}
                <span className="text-sm font-medium whitespace-nowrap">
                  {estruturas[currentIndex].label}
                </span>
              </div>
            </div>

            {/* Right slide (+1) */}
            <div
              className="hidden lg:block absolute right-[120px] w-[320px] h-[360px] rounded-[40px] overflow-hidden transition-all duration-500"
              style={{ opacity: 1 }}
            >
              <Image
                src={estruturas[getSlideIndex(1)].image}
                alt={estruturas[getSlideIndex(1)].label}
                fill
                className="object-cover"
              />
            </div>

            {/* Far right slide (+2) */}
            <div
              className="hidden lg:block absolute right-0 w-[200px] h-[280px] rounded-[40px] overflow-hidden transition-all duration-500"
              style={{ opacity: 0.4 }}
            >
              <Image
                src={estruturas[getSlideIndex(2)].image}
                alt={estruturas[getSlideIndex(2)].label}
                fill
                className="object-cover"
              />
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-center gap-4 mt-6">
          {/* Left arrow */}
          <button
            onClick={goToPrevious}
            className={`p-2 transition-colors  cursor-pointer h- ${
              isLight
                ? "hover:opacity-70"
                : isDark
                ? "text-white hover:text-white/70"
                : "text-white hover:text-white/70"
            }`}
            style={isLight ? { color: "#999" } : undefined}
            aria-label="Anterior"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M8.69471 20.9758C9.11979 20.4898 9.11979 19.6829 8.70304 19.1871L3.63459 13.2667L22.9246 13.2667C23.5165 13.2667 24 12.7028 24 12.0029C24 11.3029 23.5165 10.7391 22.9246 10.7391L3.64292 10.7391L8.71137 4.81868C9.12812 4.32289 9.11979 3.52572 8.70304 3.02993C8.27795 2.54386 7.60259 2.54386 7.17751 3.03965L0.308378 11.1085C0.216666 11.2252 0.141666 11.3515 0.0833325 11.5071C0.0249992 11.6626 0 11.8279 0 11.9932C0 12.3237 0.108333 12.6348 0.308378 12.8778L7.17751 20.9467C7.58593 21.4522 8.26962 21.4619 8.69471 20.9758Z" />
            </svg>
          </button>

          {/* Dots */}
          <div className="flex gap-2.5 items-center py-2">
            {estruturas.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`rounded-full transition-all cursor-pointer ${
                  index === currentIndex
                    ? "bg-secondary-main w-3 h-3"
                    : isLight
                    ? "bg-gray-300 w-2.5 h-2.5 hover:bg-gray-400"
                    : isDark
                    ? "bg-white/30 w-2.5 h-2.5 hover:bg-secondary-main"
                    : "bg-white/30 w-2.5 h-2.5 hover:bg-white"
                }`}
                aria-label={`Ir para estrutura ${index + 1}`}
              />
            ))}
          </div>

          {/* Right arrow */}
          <button
            onClick={goToNext}
            className={`p-2 transition-colors  cursor-pointer h- ${
              isLight
                ? "hover:opacity-70"
                : isDark
                ? "text-white hover:text-white/70"
                : "text-white hover:text-white/70"
            }`}
            style={isLight ? { color: "#999" } : undefined}
            aria-label="Próximo"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M15.3053 3.02416C14.8802 3.51023 14.8802 4.31712 15.297 4.81291L20.3654 10.7333H1.07537C0.483501 10.7333 0 11.2972 0 11.9971C0 12.6971 0.483501 13.2609 1.07537 13.2609H20.3571L15.2886 19.1813C14.8718 19.6771 14.8802 20.4743 15.297 20.9701C15.7221 21.4561 16.3974 21.4561 16.8225 20.9603L23.6916 12.8915C23.7833 12.7748 23.8583 12.6485 23.9166 12.4929C23.975 12.3374 24 12.1721 24 12.0068C24 11.6763 23.8916 11.3652 23.6916 11.1222L16.8225 3.05332C16.414 2.5478 15.7305 2.53808 15.3053 3.02416Z" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
}
